import {
    type App,
    computed,
    type ComputedRef,
    inject,
    toValue,
    type WritableComputedRef,
} from 'vue'
import { type CookieStore } from '@/plugins/cookie-store.ts'
import { type AppCurrency, isAppCurrency } from '@/plugins/currency.ts'
import {
    booleanToOptionalStringTransformer,
    type ModelTransformer,
    optionalBooleanToStringTransformer,
} from '@/helpers/transformer.ts'
import type { RefOrGetter } from '@/helpers'
import type { CookieOptions, CookieProvider } from '@/app-config.ts'

const cookieSymbol: unique symbol = Symbol()

export type Cookie<T> = {
    [cookieSymbol]: true
    value: T
    options?: CookieOptions
}

type MakeCookieOptions<T extends Record<string, any>> = {
    [key in keyof T]: T[key] | Cookie<T[key]> | undefined
}

export type ReactiveCookies<T extends Record<string, string | undefined>> = {
    [key in keyof T]: ReactiveCookie<
        T[key] extends undefined ? string | undefined : string
    >
}

type CookieSetterArgument<T> = T | Cookie<T>
export type ReactiveCookie<T> = WritableComputedRef<T, CookieSetterArgument<T>>

export function useCookie(
    name: string,
    value: string | Cookie<string> | undefined = undefined
): ReactiveCookie<string> {
    const { createCookie } = useCookiePlugin()

    return createCookie(name, value)
}

export function toBooleanCookie(
    cookie: ReactiveCookie<string>
): ComputedRef<boolean> {
    return computed(() => !!cookie.value)
}

/**
 * Keys are cookie names, values are cookie values.
 * chat_started // original cookie value will be preserved
 * asd // will have `dsa` if the cookie is empty(undefined)
 * const { chat_started, asd } = useCookies({
 * 'chat_started': undefined,
 * 'asd': 'dsa'
 * })
 **/
export function useCookies<T extends Record<string, string | undefined>>(
    cookies: MakeCookieOptions<T>
): ReactiveCookies<T> {
    const { createCookies } = useCookiePlugin()

    return createCookies(cookies)
}

export function useObjectCookie<T>(
    name: string,
    defaultValue: T | undefined = undefined
): ReactiveCookie<T> {
    return createObjectCookie(name, defaultValue, useCookiePlugin())
    // const { provider, store } = useCookiePlugin()
    //
    // const cookieString = provider.get(name)
    //
    // if (cookieString !== undefined) {
    //     try {
    //         store.value[name] = cookieString && JSON.parse(cookieString)
    //     } catch {
    //         store.value[name] = cookieString
    //     }
    // } else if (typeof defaultValue === 'string') {
    //     provider.set(name, defaultValue, undefined)
    //     store.value[name] = defaultValue
    // } else if (defaultValue !== undefined) {
    //     provider.set(name, JSON.stringify(defaultValue), undefined)
    //     store.value[name] = defaultValue
    // }
    //
    // return computed({
    //     get: () => store.value[name],
    //     set: (cookie) => {
    //         const { value, options } = toCookieObject(cookie)
    //
    //         store.value[name] = value
    //
    //         provider.set(
    //             name,
    //             typeof value === 'string' ? value : JSON.stringify(value),
    //             options
    //         )
    //     },
    // })
}

export function createObjectCookie<T>(
    name: string,
    defaultValue: T | undefined = undefined,
    plugin: ReactiveCookiePlugin
): ReactiveCookie<T> {
    const { provider, store } = plugin

    const cookieString = provider.get(name)

    if (cookieString !== undefined) {
        try {
            store.value[name] = cookieString && JSON.parse(cookieString)
        } catch {
            store.value[name] = cookieString
        }
    } else if (typeof defaultValue === 'string') {
        provider.set(name, defaultValue, undefined)
        store.value[name] = defaultValue
    } else if (defaultValue !== undefined) {
        provider.set(name, JSON.stringify(defaultValue), undefined)
        store.value[name] = defaultValue
    }

    return computed({
        get: () => store.value[name],
        set: (cookie) => {
            const { value, options } = toCookieObject(cookie)

            store.value[name] = value

            provider.set(
                name,
                typeof value === 'string' ? value : JSON.stringify(value),
                options
            )
        },
    })
}

export function useBooleanCookie(
    name: string,
    value?: boolean
): ReactiveCookie<boolean> {
    const { createBooleanCookie } = useCookiePlugin()

    return createBooleanCookie(name, value)
}

export function useOptionalBooleanCookie(
    name: string,
    value?: boolean
): ReactiveCookie<boolean | undefined> {
    const { createCookie } = useCookiePlugin()
    const cookie = createCookie(
        name,
        optionalBooleanToStringTransformer.transform(value)
    )

    return createTransformedCookie(cookie, optionalBooleanToStringTransformer)
}

export function createTransformedCookie<Model, Storage>(
    cookie: ReactiveCookie<Storage>,
    transformer: ModelTransformer<Model, Storage>
): ReactiveCookie<Model> {
    return computed({
        get() {
            return transformer.reverseTransform(cookie.value)
        },
        set(value) {
            const asCookie = toCookieObject(value)
            cookie.value = makeCookie(
                transformer.transform(asCookie.value),
                asCookie.options
            )
        },
    })
}

export function useCurrencyCookie(): ReactiveCookie<AppCurrency | undefined> {
    const { createCurrencyCookie } = useCookiePlugin()

    return createCurrencyCookie()
}

export function useIsAdminCookie(): ReactiveCookie<boolean> {
    const { createIsAdminCookie } = useCookiePlugin()

    return createIsAdminCookie()
}

export function useCookiesDismissedCookie(): ReactiveCookie<boolean> {
    return useBooleanCookie('cookies_dimissed')
}

export function useHideGeneralWarningsCookie(): ReactiveCookie<boolean> {
    return useBooleanCookie('hide_general_warnings')
}

export interface ReactiveCookiePlugin {
    provider: CookieProvider
    store: CookieStore

    createCookie<T extends string | undefined>(
        name: string,
        defaultValue?: T | Cookie<T>
    ): ReactiveCookie<T extends undefined ? T : Exclude<T, undefined>>

    createCookies<T extends Record<string, string | undefined>>(
        cookie: MakeCookieOptions<T>
    ): ReactiveCookies<T>

    createBooleanCookie(name: string, value?: boolean): ReactiveCookie<boolean>

    createCurrencyCookie(): ReactiveCookie<AppCurrency | undefined>

    createIsAdminCookie(): ReactiveCookie<boolean>
}

const key = 'app.plugin.cookie-factory'

export function installReactiveCookiePlugin(
    app: App,
    provider: CookieProvider,
    store: CookieStore
): ReactiveCookiePlugin {
    function createCookie<T extends string | undefined>(
        name: string,
        defaultValue?: T | Cookie<T>
    ): ReactiveCookie<T extends undefined ? T : Exclude<T, undefined>> {
        const currentValue = provider.get(name)

        if (undefined === currentValue && undefined !== defaultValue) {
            const defaultValueCookie = toCookieObject(defaultValue)

            provider.set(
                name,
                defaultValueCookie.value,
                defaultValueCookie.options
            )
            store.value[name] = defaultValueCookie.value
        } else {
            store.value[name] = currentValue
        }

        return computed({
            get: () => {
                return store.value[name]
            },
            set: (value) => {
                const cookie = toCookieObject(value)

                store.value[name] = cookie.value
                provider.set(name, cookie.value, cookie.options)
            },
        })
    }

    function createCookies<T extends Record<string, string | undefined>>(
        cookie: MakeCookieOptions<T>
    ): ReactiveCookies<T> {
        return Object.entries(cookie).reduce((acc, cookie) => {
            const [name, defaultValue]: [
                string,
                string | Cookie<string | undefined> | undefined,
            ] = cookie

            return {
                ...acc,
                [name]: createCookie(name, defaultValue),
            }
        }, {} as ReactiveCookies<T>)
    }

    function createCurrencyCookie(): ReactiveCookie<AppCurrency | undefined> {
        return createTransformedCookie(createCookie('currency'), {
            transform(value) {
                return value
            },
            reverseTransform(value) {
                if (undefined !== value && isAppCurrency(value)) {
                    return value
                }

                return undefined
            },
        })
    }

    function createBooleanCookie(
        name: string,
        value?: boolean
    ): ReactiveCookie<boolean> {
        const cookie = createCookie(
            name,
            booleanToOptionalStringTransformer.transform(value ?? false)
        )

        return createTransformedCookie(
            cookie,
            booleanToOptionalStringTransformer
        )
    }

    function createIsAdminCookie(): ReactiveCookie<boolean> {
        return createBooleanCookie('admin_activity')
    }

    const plugin: ReactiveCookiePlugin = {
        provider,
        store,

        createCookie,
        createCookies,

        createBooleanCookie,

        createCurrencyCookie,
        createIsAdminCookie,
    }

    app.provide(key, plugin)

    return plugin
}

export function makeCookie<T>(value: T, options?: CookieOptions): Cookie<T> {
    return {
        [cookieSymbol]: true,
        value,
        options: {
            secure: true,
            sameSite: 'lax',
            ...options,
            path: options?.path === undefined ? '/' : options?.path,
        },
    }
}

function useCookiePlugin(): ReactiveCookiePlugin {
    return inject(key) as ReactiveCookiePlugin
}

function toCookieObject<T>(cookie: T | Cookie<T>): Cookie<T> {
    if (
        cookie !== null &&
        typeof cookie === 'object' &&
        cookieSymbol in cookie
    ) {
        return cookie
    }

    return makeCookie(cookie)
}

export function useVariableNameCookie(
    name: RefOrGetter<string>,
    value: string | undefined = undefined
): ReactiveCookie<string> {
    const { createCookie } = useCookiePlugin()

    const cookie = computed(() => createCookie(toValue(name), value))

    return computed({
        get() {
            return cookie.value.value
        },
        set(value) {
            cookie.value.value = value
        },
    })
}

export function useNavButtonCookie(
    name: RefOrGetter<string>
): ReactiveCookie<string> {
    return useVariableNameCookie(
        computed(() => {
            return `qa.nav.${toValue(name)}`
        }),
        '/'
    )
}
